import React, {
  FC,
  useState,
  useEffect,
  ChangeEvent,
  useCallback,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../context/UserContext';

import {
  getPrivateEvent,
  updateEvent,
  deleteEvent,
  closeEvent,
  getEventsMiniature,
  createTag,
  findTag,
  publishEvent,
} from '../../utils/api';
import {
  Track,
  PublicEvent,
  PublicUser,
  Tag_event,
} from '../../utils/EventsProperties';
import logger from '../../utils/logger';

import NavigateBackButton from '../../components/ReworkComponents/Button/NavigateBackButton/NavigateBackButton';
import Button, {
  ButtonType,
} from '../../components/ReworkComponents/generic/Button/Button';
import Modal from '../../components/ReworkComponents/generic/modal/modal';
import InputFile from '../../components/ReworkComponents/inputFile/InputFile';
import EventBox from '../../components/ReworkComponents/Event/EventBox/EventBox';
import EventDashboard from '../../components/ReworkComponents/Event/EventDashboard/EventDashboard';
import Toast from '../../components/ReworkComponents/generic/Toast/Toast';
import SuggestionBar, {
  SuggestionType,
  Suggestion,
} from '../../components/ReworkComponents/video/admin/SuggestionBar/SuggestionBar';
import Tag, {
  TagType,
} from '../../components/ReworkComponents/generic/Tag/Tag';

import { useBrandingContext } from '../../context/BrandingContext';
import { EventStatus } from '../../utils/EventStatus';

import styles from './eventSettings.module.css';

const EventSettings: FC = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useUser();
  const { getImageUrl } = useBrandingContext();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [startHour, setStartHour] = useState('');
  const [endHour, setEndHour] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [tracks, setTracks] = useState<Track[]>([]);
  const [tags, setTags] = useState<Tag_event[]>([]);
  const [isFetchError, setIsFetchError] = useState(false);
  const [creatorId, setCreatorId] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type?: 'success' | 'error' | 'info';
  } | null>(null);

  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [previewMiniatureUrl, setPreviewMiniatureUrl] = useState<string | null>(
    null,
  );
  const [fallbackMiniatureUrl, setFallbackMiniatureUrl] = useState<
    string | null
  >(null);
  const [eventClosed, setEventClosed] = useState(false);
  const [errorName, setErrorName] = useState(false);
  const [errorDescription, setErrorDescription] = useState(false);
  const [isButtonDisabled, setButtonDisabled] = useState(true);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);

  const userString = localStorage.getItem('backendUser');
  const userId = userString ? JSON.parse(userString)?.id || '' : '';

  useEffect(() => {
    const fetchFallbackMiniature = async () => {
      try {
        const url = await getImageUrl('default_miniature_image');
        setFallbackMiniatureUrl(url);
      } catch (error) {
        console.error('Error fetching fallback miniature:', error);
      }
    };
    fetchFallbackMiniature();
  }, [getImageUrl]);

  const isTagVideo = (suggestion: Suggestion): suggestion is Tag_event => {
    return 'id' in suggestion && 'name' in suggestion;
  };

  const selectTag = (tagChoosen: Suggestion) => {
    if (
      isTagVideo(tagChoosen) &&
      tags.some((tag) => tag.name === tagChoosen.name)
    ) {
      setToast({
        message: t('tagAlreadyExists') + '.',
        type: 'info',
      });
      return;
    }
    setTags([...tags, tagChoosen as Tag_event]);
    setButtonDisabled(false);
  };

  const handleDeleteTag = (name: string) => {
    setTags(tags.filter((tag) => tag.name !== name));
    setButtonDisabled(false);
  };

  const addTag = (query: string) => {
    createTag({ name: query })
      .then(async (response) => {
        if (
          response.status === 202 ||
          response.status === 201 ||
          response.status === 204 ||
          response.status === 200
        ) {
          setToast({
            message: t('tagCreated'),
            type: 'success',
          });
          const response = await findTag(query);
          const newTag = response.data[0];
          if (!tags.some((tag) => tag.name === newTag.name)) {
            setTags([...tags, newTag]);
            setButtonDisabled(false);
          }
        } else {
          setToast({
            message: t('failedLoading') + `:${response}`,
            type: 'error',
          });
        }
      })
      .catch((error) => {
        console.error('Erreur lors de la création du tag :', error);
        setToast({
          message: t('failedLoadingVideo'),
          type: 'error',
        });
      });
  };

  const fetchMiniature = useCallback(async () => {
    if (!eventId) return;

    try {
      const res = await getEventsMiniature(eventId);
      if (res?.data?.url && !res.data.url.includes('imageSlug')) {
        setPreviewMiniatureUrl(res.data.url);
      } else {
        setPreviewMiniatureUrl(
          fallbackMiniatureUrl || '/exemple/image_tuile_event.png',
        );
      }
    } catch (err) {
      console.error(`Erreur récupération miniature pour event ${eventId}`, err);
      setPreviewMiniatureUrl(
        fallbackMiniatureUrl || '/exemple/image_tuile_event.png',
      );
    }
  }, [eventId, fallbackMiniatureUrl]);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await getPrivateEvent(eventId);
        if (res.status === 200) {
          const data = res.data;
          setName(data.name);
          setDescription(data.description);

          const start = new Date(data.startDate);
          const end = new Date(data.endDate);

          const localYear = start.getFullYear();
          const localMonth = String(start.getMonth() + 1).padStart(2, '0');
          const localDay = String(start.getDate()).padStart(2, '0');
          setDate(`${localYear}-${localMonth}-${localDay}`);

          setStartHour(
            `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`,
          );
          setEndHour(
            `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`,
          );

          setTracks(data.tracks);
          setTags(data.tags);
          setCreatorId(data.creator.id);
          setEventClosed(data.closed);
          setIsPublished(data.published);
        }
      } catch (error) {
        logger.error({ err: error }, 'Error fetching event');
        setIsFetchError(true);
      }
    };
    fetchEvent();
    fetchMiniature();
  }, [eventId, fetchMiniature]);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedImage(file);
    setButtonDisabled(false);
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === 'string' && setImageUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    setErrorName(!name.trim());
    setErrorDescription(!description.trim());

    if (!name.trim() || !description.trim()) {
      setToast({
        message: t('NameDescriptionRequired'),
        type: 'error',
      });
      return;
    }

    const [year, month, day] = date.split('-');
    const [s_hour, s_minute] = startHour.split(':');
    const [e_hour, e_minute] = endHour.split(':');

    const startDateISOString = new Date(
      +year,
      +month - 1,
      +day,
      +s_hour,
      +s_minute,
    ).toISOString();
    const endDateISOString = new Date(
      +year,
      +month - 1,
      +day,
      +e_hour,
      +e_minute,
    ).toISOString();

    if (new Date(startDateISOString) >= new Date(endDateISOString)) {
      setToast({
        message: t('StartTimeError'),
        type: 'error',
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append(
        'image_slug',
        selectedImage?.name || fallbackMiniatureUrl || 'default-image',
      );
      formData.append('name', name);
      formData.append('description', description);

      formData.append('startDate', startDateISOString);
      formData.append('endDate', endDateISOString);
      formData.append('tags', JSON.stringify(tags));
      if (selectedImage) formData.append('miniature', selectedImage);

      const res = await updateEvent(eventId, formData);
      if (res.status === 200) {
        const refreshedEvent = await getPrivateEvent(eventId);
        if (refreshedEvent.status === 200) {
          setTracks(refreshedEvent.data.tracks);
        }
        setToast({
          message: t('eventModified'),
          type: 'success',
        });
        setButtonDisabled(true);
      } else {
        setToast({
          message: t('eventCouldNotBeModified'),
          type: 'error',
        });
      }
    } catch (error) {
      logger.error({ err: error }, 'Error updating event');
      setToast({
        message: t('eventCouldNotBeModified'),
        type: 'error',
      });
    }
  };

  const deleteEventHandler = async () => {
    try {
      const res = await deleteEvent(eventId);
      if (res.status === 200) {
        setToast({
          message: t('eventDeletedSuccessfully'),
          type: 'success',
        });
        setTimeout(() => {
          navigate('/my-events');
        }, 1000);
      }
    } catch (error) {
      setToast({
        message: t('eventCouldNotBeDeleted'),
        type: 'error',
      });
    }
  };

  const handleCloseEvent = async () => {
    for (const track of tracks) {
      if (!track.closed) {
        setModalMessage(t('impossibleCloseEvent'));
        return;
      }
    }
    try {
      const res = await closeEvent(eventId);
      if (res.status === 200) {
        setEventClosed(true);
        setIsCloseModalOpen(false);
        setToast({
          message: t('eventClosed'),
          type: 'success',
        });

        setTimeout(() => {
          navigate('/events-home');
        }, 1000);
      }
    } catch {
      setToast({
        message: t('eventCouldNotClosed'),
        type: 'error',
      });
    }
  };

  const handlePublishEvent = async () => {
    try {
      const res = await publishEvent(eventId);
      if (res.status === 200) {
        setIsPublished(true);
        setToast({
          message: t('EventPublished'),
          type: 'success',
        });
      }
    } catch (error) {
      setToast({
        message: t('EventCouldNotBePublished'),
        type: 'error',
      });
    }
  };

  const getPreviewEvent = (): PublicEvent => {
    const now = new Date();
    // Sans le "Z" pour que la prévisualisation soit dans le fuseau horaire local
    const startDate =
      date && startHour ? new Date(`${date}T${startHour}:00`) : now;
    const endDate = date && endHour ? new Date(`${date}T${endHour}:00`) : now;

    const creator: PublicUser = {
      id: user?.id || creatorId,
      firstName: user?.firstName || '...',
      lastName: user?.lastName || '...',
    };

    return {
      id: eventId || '',
      name: name || t('NameEvent'),
      description,
      tags: tags,
      startDate,
      endDate,
      published: false,
      private: true,
      closed: eventClosed,
      imageSlug: imageUrl ? imageUrl : '',
      tracks: [],
      creatorId,
      canBeEditByUser: true,
      creator,
      nbSubscription: 0,
    };
  };

  const handleShareEvent = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!isPublished) {
      setToast({
        message: t('eventMustBePublished'),
        type: 'error',
      });
      return;
    }
    const invitationLink = `${window.location.origin}/events/${eventId}/tracks`;
    try {
      await navigator.clipboard.writeText(invitationLink);
      setToast({
        message: t('linkCopied'),
        type: 'success',
      });
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = invitationLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setToast({
        message: t('linkCopied'),
        type: 'success',
      });
    }
  };

  if (isFetchError) {
    return (
      <Button
        label={t('eventUnavailableReturnPresentationPage')}
        onClick={() => navigate('/my-events')}
      />
    );
  }

  return (
    <div className={styles.pageEventSettings}>
      <EventDashboard
        eventId={eventId}
        tracks={tracks}
        eventClosed={!eventClosed && userId === creatorId}
      />

      <div className={styles.eventsContainer}>
        <div className={styles.topLayout}>
          <div className={styles.titleLayout}>
            <NavigateBackButton />
            <h1>{t('EditEvent')}</h1>
          </div>
        </div>

        <div className={styles.mainContentWrapper}>
          <div className={styles.eventSettings}>
            <div className={styles.formSection}>
              <h3>{t('BasicInformation')}</h3>
              <div className={styles.inputWrapper}>
                <label>{t('NameEvent')}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setButtonDisabled(false);
                  }}
                  disabled={eventClosed}
                  className={errorName ? styles.error : ''}
                />
              </div>
              <div className={styles.inputWrapper}>
                <label>{t('EventDescription')}</label>
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setButtonDisabled(false);
                  }}
                  className={errorDescription ? styles.error : ''}
                  disabled={eventClosed}
                />
              </div>
              <div className={styles.inputWrapper}>
                <label>{t('Thumbnail')}</label>
                <InputFile
                  placeholder={t('ChooseThumbnail')}
                  onChange={handleImageChange}
                  disable={eventClosed}
                />
              </div>
            </div>

            <div className={styles.formSection}>
              <h3>{t('Schedule')}</h3>
              <div className={styles.inputWrapper}>
                <label>{t('DateEvent')}</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                    setButtonDisabled(false);
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  disabled={eventClosed}
                />
              </div>
              <div className={styles.inputWrapper}>
                <label>{t('StartEvent')}</label>
                <input
                  type="time"
                  value={startHour}
                  onChange={(e) => {
                    setStartHour(e.target.value);
                    setButtonDisabled(false);
                  }}
                  disabled={eventClosed}
                />
              </div>
              <div className={styles.inputWrapper}>
                <label>{t('EndEvent')}</label>
                <input
                  type="time"
                  value={endHour}
                  onChange={(e) => {
                    setEndHour(e.target.value);
                    setButtonDisabled(false);
                  }}
                  disabled={eventClosed}
                />
              </div>
            </div>

            <div className={styles.formSection}>
              <h3>{t('Tags')}</h3>
              <SuggestionBar
                onClick={selectTag}
                placeholder="Tags"
                type={SuggestionType.TAG}
                name="suggestionTag"
                onAdd={addTag}
              />
              <div className={styles.tagList}>
                {tags.map((tag, index) => (
                  <Tag
                    key={index}
                    content={tag.name}
                    type={TagType.DEFAULT}
                    editable={!eventClosed}
                    delete={handleDeleteTag}
                  />
                ))}
              </div>
            </div>

            {!eventClosed && (
              <div className={styles.formSection}>
                <h3>{t('Actions')}</h3>
                <div className={styles.confirmationButtons}>
                  <Button
                    label={t('Save')}
                    type={
                      isButtonDisabled
                        ? ButtonType.disabled
                        : ButtonType.primary
                    }
                    onClick={handleSubmit}
                  />
                  {!isPublished ? (
                    <Button
                      label={t('PublishEvent')}
                      onClick={handlePublishEvent}
                      type={ButtonType.primary}
                    />
                  ) : (
                    <Button
                      label={t('CloseEvent')}
                      onClick={() => setIsCloseModalOpen(true)}
                      type={ButtonType.danger}
                    />
                  )}
                  <Button
                    label={t('shareEvent')}
                    onClick={handleShareEvent}
                    type={ButtonType.secondary}
                  />
                  {/*
                  <Button
                    label={t('DeleteEvent')}
                    onClick={() => setIsDeleteOpen(true)}
                    type={ButtonType.danger}
                  />*/}
                </div>
              </div>
            )}
          </div>

          <div className={styles.preview}>
            <h2>{t('Preview')}</h2>
            <EventBox
              event={getPreviewEvent()}
              imageURL={
                imageUrl ||
                previewMiniatureUrl ||
                fallbackMiniatureUrl ||
                '/exemple/image_tuile_event.png'
              }
              eventStatus={EventStatus.Preview}
            />
          </div>
        </div>
      </div>

      <Modal
        isOpen={isCloseModalOpen}
        toggle={() => setIsCloseModalOpen(false)}
      >
        <h2>{t('SureCloseEvent')}</h2>
        <div className={styles.confirmationButtons}>
          <Button label={t('Confirm')} onClick={handleCloseEvent} />
          <Button
            label={t('Cancel')}
            onClick={() => setIsCloseModalOpen(false)}
          />
        </div>
        {modalMessage && <p>{modalMessage}</p>}
      </Modal>

      <Modal isOpen={isDeleteOpen} toggle={() => setIsDeleteOpen(false)}>
        <h2>{t('SureDeleteEvent')}</h2>
        <div className={styles.confirmationButtons}>
          <Button label={t('Delete')} onClick={deleteEventHandler} />
          <Button label={t('Cancel')} onClick={() => setIsDeleteOpen(false)} />
        </div>
      </Modal>

      {toast && (
        <Toast
          key={`${toast.message}-${toast.type}`}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default EventSettings;
